import React from 'react';
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Autocomplete,
  Popper,
  styled,
} from "@mui/material";
import { Controller, Control, FieldError } from "react-hook-form";

const StyledPopper = styled(Popper)({
  maxHeight: '260px',
  fontSize: '11px',
  '& .MuiAutocomplete-paper': {
    borderRadius: '8px',
    border: '1px solid #B6C4E3',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  '& .MuiAutocomplete-option': {
    fontSize: '11px',
    color: '#5C79AA',
    border: '1px solid #B6C4E3',
    '&:hover': {
      backgroundColor: '#f0f4f8',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#5C79AA',
      color: 'white',
    },
  },
});

interface CustomRadioGroupProps {
  name: string;
  control: Control<any>;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  rules?: any;
  onChange?: (value: string) => void;
  error?: FieldError;
}

export const CustomRadioGroup: React.FC<CustomRadioGroupProps> = ({
  name,
  control,
  label,
  options,
  required = false,
  disabled = false,
  rules,
  onChange,
  error
}) => {
  return (
    <FormControl sx={{ marginBottom: 2 }} >
      <div className="radio-section">
        <p className="mb-0" style={{width: '100px', fontSize: '14px'}}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
        </p>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <RadioGroup
              row
              {...field}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  style={{
                    width:'180px',
                    fontSize: '14px'
                  }}
                  control={
                    <Radio
                      disabled={disabled}
                      size='small'
                      sx={{
                        color: '#5C79AA',
                        '&.Mui-checked': {
                          color: '#5C79AA',
                        },
                        '&.Mui-disabled': {
                          color: '#B6C4E3',
                        },
                      }}
                    />
                  }
                  label={option.label}
                  sx={{
                    fontSize: '10px',
                    color: '#5C79AA',
                    marginRight: '24px',
                    '&.Mui-disabled': {
                      color: '#B6C4E3',
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px'
                    }
                  }}
                />
              ))}
            </RadioGroup>
          )}
        />
      </div>
      {error && (
        <span style={{ color: '#dc3545', fontSize: '9px', marginTop: '4px', display: 'block' }}>{error.message}</span>
      )}
    </FormControl>
  );
};

interface CustomTextFieldProps {
  name: string;
  control: Control<any>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  rules?: any;
  width?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}

export const CustomTextField: React.FC<CustomTextFieldProps> = ({
  name,
  control,
  label,
  required = false,
  disabled = false,
  rules,
  width = '100%',
  multiline = false,
  rows = 1,
  placeholder
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          id="standard-basic"
          label={label}
          variant="standard"
          multiline={multiline}
          rows={rows}
          placeholder={placeholder}
          inputProps={{
            readOnly: disabled,
            style: {
              fontSize: '13px',
              color: '#5C79AA',
            }
          }}
          sx={{
            width: width,
            marginBottom: 2,
            '& .MuiFormLabel-asterisk': {
              color: 'red',
            },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
              fontWeight: '500',
              color: '#6c757d',
              '&.Mui-focused': {
                color: '#5C79AA',
              },
              '&.Mui-disabled': {
                color: '#B6C4E3',
              },
            },
            '& .MuiInput-root': {
              fontSize: '11px',
              color: '#5C79AA',
              '&:before': {
                borderBottom: '1px solid #B6C4E3',
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: '2px solid #5C79AA',
              },
              '&:after': {
                borderBottom: '2px solid #5C79AA',
              },
              '&.Mui-disabled': {
                color: '#B6C4E3',
                '&:before': {
                  borderBottom: '1px solid #B6C4E3',
                },
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#5C79AA',
              opacity: 0.7,
              fontSize: '11px',
            },
            '& .MuiFormHelperText-root': {
              fontSize: '11px',
              marginTop: '4px',
              '&.Mui-error': {
                color: '#dc3545',
              },
            },
          }}
          required={required}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
};

interface CustomAutocompleteProps {
  name: string;
  control: Control<any>;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  rules?: any;
  width?: string;
  placeholder?: string;
}

export const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  name,
  control,
  label,
  options,
  required = false,
  disabled = false,
  rules,
  width = '30%',
  placeholder
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          variant="standard"
          required={required}
          sx={{ 
            width: width,
          }}
        >
          <Autocomplete
            options={options || []}
            getOptionLabel={(option: any) => option.label || ''}
            isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
            disableClearable={disabled}
            readOnly={disabled}
            PopperComponent={StyledPopper}
            sx={{ 
              fontSize: '11px',
              '& .MuiInputBase-root': {
                fontSize: '11px',
                color: '#5C79AA',
                '&:before': {
                  borderBottom: '1px solid #B6C4E3',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: '2px solid #5C79AA',
                },
                '&:after': {
                  borderBottom: '2px solid #5C79AA',
                },
                '&.Mui-disabled': {
                  color: '#B6C4E3',
                  '&:before': {
                    borderBottom: '1px solid #B6C4E3',
                  },
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#5C79AA',
                opacity: 0.7,
                fontSize: '11px',
              },
            }}
            value={options?.find(option => option.label === field.value) || undefined}
            onChange={(event: any, newValue: any) => {
              field.onChange(newValue?.label || '');
            }}
            renderInput={(params: any) => (
              <TextField
                {...params}
                variant="standard"
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  readOnly: disabled,
                  style: {
                    fontSize: '11px',
                    color: '#5C79AA',
                  }
                }}
                InputLabelProps={{
                  style: { 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6c757d',
                  }
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6c757d',
                    '&.Mui-focused': {
                      color: '#5C79AA',
                    },
                    '&.Mui-disabled': {
                      color: '#B6C4E3',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11px',
                    marginTop: '4px',
                    '&.Mui-error': {
                      color: '#dc3545',
                    },
                  },
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormControl>
      )}
    />
  );
}; 